import React, { useEffect, useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css'
import styles from "../../styles/tenant.module.css"
import { useForm, useFieldArray } from 'react-hook-form'
import { useMutation, useQuery } from "@apollo/client";
import { GET_TENANT_BY_ID, UPDATE_TENANTS } from "../../graphql/TenantQueries";
import ErrorDisplay from "../common/ErrorDisplay";
import FeatureConfigurationSection from "../common/FeatureConfigurationSection";
import { TenantFormData, EditTenantModalProps, Feature, ApplicationSubscription, Alias, Contact, Tag, PublicWaterSystem } from "../../types/tenant";
import logger from "../../utils/logger";
import { safeParseFloat, safeParseInt } from "../../utils/formUtils";

function EditModal({ setOpenEditModal, setOpenActionModal, fieldData }: EditTenantModalProps) {
    const { register, handleSubmit, formState: { errors }, setValue, watch, control, trigger } = useForm<TenantFormData>({
        mode: 'onChange'
    });
    const [subscriptionFeatures, setSubscriptionFeatures] = useState<{[key: number]: Feature[]}>({});
    const isInitialized = React.useRef(false);
    const isFeaturesInitialized = React.useRef(false);
    const [updateTenant, { loading: mutationLoading, error: mutationError }] = useMutation(UPDATE_TENANTS, {
        refetchQueries: [
          {query: GET_TENANT_BY_ID, variables: { id: fieldData }  },
          'getTenants'
        ],
      });
    const { loading, error, data } = useQuery(GET_TENANT_BY_ID, { variables: { id: fieldData } })

    const { fields: contactFields, append: contactAppend, remove: contactRemove } = useFieldArray({ control, name: "contacts" });
    const { fields: aliasFields, append: aliasAppend, remove: aliasRemove } = useFieldArray({ control, name: "aliases" });
    const { fields: appFields, append: appAppend, remove: appRemove } = useFieldArray({
        control,
        name: "applicationSubscriptions",
        rules: { required: "At least one application subscription is required" }
    });
    const { fields: tagFields, append: tagAppend, remove: tagRemove } = useFieldArray({ control, name: "tags" });
    const { fields: pwsFields, append: pwsAppend, remove: pwsRemove } = useFieldArray({ control, name: "publicWaterSystems" });

    // Initialize form data when tenant data loads
    useEffect(() => {
        if (data?.getTenantById && !isInitialized.current) {
            isInitialized.current = true;
            const tenant = data.getTenantById;

            if (tenant.contacts) {
                tenant.contacts.forEach((contact: Contact) => {
                    contactAppend({
                        name: contact.name || "",
                        email: contact.email || "",
                        phone: contact.phone || "",
                        website: contact.website || "",
                        type: contact.type || "",
                        address: contact.address || "",
                        leadWebsite: contact.leadWebsite || ""
                    });
                });
            }
            if (tenant.aliases) {
                tenant.aliases.forEach((alias: Alias) => {
                    aliasAppend({
                        alias: alias.alias || "",
                        description: alias.description || "",
                        additionalParameters: typeof alias.additionalParameters === "object"
                            ? JSON.stringify(alias.additionalParameters)
                            : alias.additionalParameters || "{}",
                        beginOn: alias.beginOn || ""
                    });
                });
            }
            if (tenant.applicationSubscriptions) {
                tenant.applicationSubscriptions.forEach((subscription: ApplicationSubscription, index: number) => {
                    appAppend({ application: subscription.application || 'leadCAST', licenseLevel: subscription.licenseLevel || "" });
                    setValue(`applicationSubscriptions.${index}.application`, subscription.application || 'leadCAST');
                });
            }
            if (tenant.tags) {
                tenant.tags.forEach((tag: Tag) => {
                    tagAppend({
                        name: tag.name || "",
                        value: tag.value || ""
                    });
                });
            }
            if (tenant.publicWaterSystems) {
                tenant.publicWaterSystems.forEach((pws: PublicWaterSystem) => {
                    pwsAppend({
                        pwsId: pws.pwsId || "",
                        alias: pws.alias || "",
                        name: pws.name || "",
                        beginOn: pws.beginOn ? pws.beginOn.substring(0, 10) : ""
                    });
                });
            }
        }
    }, [data, contactAppend, aliasAppend, appAppend, tagAppend, pwsAppend])

    // Initialize subscription features when data loads
    useEffect(() => {
        if (data?.getTenantById?.applicationSubscriptions && !isFeaturesInitialized.current) {
            isFeaturesInitialized.current = true;
            const initialFeatures: {[key: number]: Feature[]} = {};
            data.getTenantById.applicationSubscriptions.forEach((subscription: ApplicationSubscription, index: number) => {
                if (subscription.features) {
                    let featuresArray: Feature[] = [];

                    // Handle different possible formats from API
                    if (Array.isArray(subscription.features)) {
                        featuresArray = subscription.features;
                    } else if (typeof subscription.features === 'string') {
                        try {
                            const trimmed = subscription.features.trim();
                            if (trimmed) {
                                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                                    featuresArray = JSON.parse(trimmed);
                                } else {
                                    featuresArray = JSON.parse('[' + trimmed + ']');
                                }
                            }
                        } catch (e) {
                            logger.error('Failed to parse subscription features', { subscriptionIndex: index, error: String(e) });
                            featuresArray = [];
                        }
                    }

                    initialFeatures[index] = featuresArray
                        .filter(Boolean)
                        .filter((f): f is Feature => typeof f === 'object' && f !== null && 'name' in f)
                        .map((feature) => ({
                            name: feature.name,
                            description: feature.description,
                            enabled: Boolean(feature.enabled),
                            options: feature.options ? structuredClone(feature.options) : {}
                        }));
                } else {
                    initialFeatures[index] = [];
                }
            });
            setSubscriptionFeatures(initialFeatures);
        }
    }, [data])

    const processFormData = (formData: TenantFormData): TenantFormData => {
        if (formData.location) {
            formData.location.latitude = safeParseFloat(formData.location.latitude);
            formData.location.longitude = safeParseFloat(formData.location.longitude);
            formData.location.zoom = safeParseInt(formData.location.zoom);
        }

        if (formData.aliases) {
            formData.aliases = formData.aliases.filter((alias: Alias) => alias && (alias.alias || alias.description));
            formData.aliases.forEach((alias: Alias) => {
                if (alias.beginOn && alias.beginOn.trim() !== "") {
                    try {
                        alias.beginOn = new Date(alias.beginOn).toISOString();
                    } catch (_error) {
                        logger.warn('Invalid date format for alias beginOn', { value: alias.beginOn });
                        delete alias.beginOn;
                    }
                } else {
                    delete alias.beginOn;
                }
                if (!alias.additionalParameters || alias.additionalParameters.trim() === "") {
                    alias.additionalParameters = "{}";
                }
            });
        }

        if (formData.publicWaterSystems) {
            formData.publicWaterSystems = formData.publicWaterSystems.filter((pws: PublicWaterSystem) => pws && pws.pwsId);
            formData.publicWaterSystems.forEach((pws: PublicWaterSystem) => {
                if (pws.beginOn && pws.beginOn.trim() !== "") {
                    try {
                        pws.beginOn = new Date(pws.beginOn).toISOString();
                    } catch (_error) {
                        logger.warn('Invalid date format for PWS beginOn', { value: pws.beginOn });
                        delete pws.beginOn;
                    }
                } else {
                    delete pws.beginOn;
                }
            });
        }

        if (formData.tags) formData.tags = formData.tags.filter((tag: Tag) => tag && (tag.name || tag.value));
        if (formData.contacts) formData.contacts = formData.contacts.filter((contact: Contact) => contact && (contact.name || contact.email));

        return formData;
    };

    const onSubmit = async (formData: TenantFormData) => {
        if (!formData.applicationSubscriptions || formData.applicationSubscriptions.length === 0) {
            setValue('applicationSubscriptions', []);
            trigger('applicationSubscriptions');
            return;
        }

        if (!data?.getTenantById) {
            logger.error("Tenant data is not available.");
            return;
        }

        try {
            const clonedData: TenantFormData = structuredClone(formData);
            clonedData.name = data.getTenantById.name;

            if (clonedData.applicationSubscriptions) {
                clonedData.applicationSubscriptions.forEach((subscription: ApplicationSubscription, index: number) => {
                    const featuresFromState = subscriptionFeatures[index] || [];

                    if (featuresFromState.length > 0) {
                        const cleanedFeatures = featuresFromState.map(feature => ({
                            name: feature.name?.trim() || "",
                            description: feature.description,
                            enabled: Boolean(feature.enabled),
                            options: feature.options || {}
                        })).filter(feature => feature.name);

                        subscription.features = cleanedFeatures.map(feature => JSON.stringify(feature)).join(',');
                    } else {
                        subscription.features = "";
                    }
                });
            }

            const processedData = processFormData(clonedData);
            await updateTenant({ variables: { id: fieldData, input: processedData } });
            setOpenActionModal(false);
            setOpenEditModal(false);
        } catch (error) {
            logger.error('Error updating tenant', { error: String(error) });
        }
    };

    if (loading) return <h3>Loading...</h3>
    if (error) return <ErrorDisplay error={error} />;
    if (!data?.getTenantById) return <h3>Tenant not found</h3>;
    return (
        <div className={styles["modalBackground"]}>
            <div className={styles["formModalContainer"]}>
                <div className={styles["title"]}>
                    <h1>Edit Tenant</h1>
                    <h3 style={{ paddingBottom: '20px' }}>{data?.getTenantById?.name ? data.getTenantById.name : ""}</h3>
                </div>
                <hr></hr>
                <div className={styles["body"]}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <label className={styles["label"]}>Timezone</label>
                            <select {...register('timezone')} className={styles["input"]} defaultValue={data?.getTenantById?.timezone ? data.getTenantById.timezone : "America/New_York"}>
                                <option value="America/New_York">America/New York</option>
                                <option value="America/Chicago">America/Chicago</option>
                                <option value="America/Los_Angeles">America/Los Angeles</option>
                                <option value="America/Alaska">America/Alaska</option>
                                <option value="America/Arizona">America/Arizona</option>
                                <option value="America/Hawaii">America/Hawaii</option>
                            </select>
                        </div>
                        <div>
                            <label className={styles["label"]}>Display Name</label>
                            <input
                                {...register('displayName')}
                                className={styles["input"]}
                                defaultValue={data?.getTenantById?.displayName || ""}
                            />
                        </div>
                        <div>
                            <label className={styles["label"]}>Tenant Name</label>
                            <input
                                {...register('name')}
                                className={styles["input"]}
                                defaultValue={data?.getTenantById?.name || ""}
                                readOnly
                            />
                        </div>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Contacts</h3>
                        {contactFields.map((field, index) => {
                            return (
                                <div key={field.id}>
                                    <label className={styles["label"]}>Contact Name</label>
                                    <input
                                        {...register(`contacts.${index}.name`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Contact Email</label>
                                    <input
                                        {...register(`contacts.${index}.email`)}
                                        className={styles["input"]}
                                        type="email"
                                    />
                                    <label className={styles["label"]}>Contact Website</label>
                                    <input
                                        {...register(`contacts.${index}.website`)}
                                        className={styles["input"]}
                                    />
                                    <div>
                                        <label className={styles["label"]}>Contact Phone</label>
                                        <input
                                            {...register(`contacts.${index}.phone`)}
                                            className={styles["input"]}
                                        />
                                        <label className={styles["label"]}>Contact Type</label>
                                        <input
                                            {...register(`contacts.${index}.type`)}
                                            className={styles["input"]}
                                        />
                                        <label className={styles["label"]}>Contact Address</label>
                                        <input
                                            {...register(`contacts.${index}.address`)}
                                            className={styles["input"]}
                                        />
                                    </div>
                                    <div>
                                        <label className={styles["label"]}>Contact Lead Website</label>
                                        <input
                                            {...register(`contacts.${index}.leadWebsite`)}
                                            className={styles["input"]}
                                        />
                                    </div>
                                    <button type="button" onClick={() => contactRemove(index)} className={styles["cnclButton"]} style={{width: "100px", height: "35px", fontSize: "14px"}}>
                                        Delete
                                    </button>
                                    <hr />
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => contactAppend({ name: "", email: "", phone: "", website: "", type: "", address: "", leadWebsite: "" })} className={styles["button"]} style={{width: "150px", height: "35px", fontSize: "14px"}}>
                            Add Contact
                        </button>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Aliases</h3>
                        {aliasFields.map((field, index) => {
                            return (
                                <div key={field.id}>
                                    <label className={styles["label"]}>Alias</label>
                                    <input
                                        {...register(`aliases.${index}.alias`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Description</label>
                                    <input
                                        {...register(`aliases.${index}.description`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Additional Parameters</label>
                                    <input
                                        {...register(`aliases.${index}.additionalParameters`)}
                                        className={styles["input"]}
                                    />
                                    <button type="button" onClick={() => aliasRemove(index)} className={styles["cnclButton"]} style={{width: "100px", height: "35px", fontSize: "14px"}}>
                                        Delete
                                    </button>
                                    <hr />
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => aliasAppend({ alias: "", description: "", additionalParameters: "{}", beginOn: "" })} className={styles["button"]} style={{width: "150px", height: "35px", fontSize: "14px"}}>
                            Add Alias
                        </button>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Application Subscriptions *</h3>
                        {errors.applicationSubscriptions && (
                            <div style={{color: 'red', fontSize: '14px', marginBottom: '10px'}}>{errors.applicationSubscriptions.message || "At least one application subscription is required"}</div>
                        )}
                        {appFields.map((field, index) => {
                            return (
                                <div key={field.id}>
                                    <label className={styles["label"]}>Application</label>
                                    <select
                                        className={styles["input"]}
                                        style={{ marginRight: "10px" }}
                                        defaultValue={field.application}
                                        {...register(`applicationSubscriptions.${index}.application`)}
                                    >
                                        <option value="leadCAST">leadCAST</option>
                                        <option value="pipeCAST">pipeCAST</option>
                                        <option value="epiCAST">epiCAST</option>
                                        <option value="proteus">Raini</option>
                                        <option value="assetCAST">assetCAST</option>
                                        <option value="datacast">dataCAST</option>
                                    </select>
                                    <label className={styles["label"]}>License Level</label>
                                    <input
                                        {...register(`applicationSubscriptions.${index}.licenseLevel`)}
                                        className={styles["input"]}
                                    />
                                    <FeatureConfigurationSection
                                        value={subscriptionFeatures[index] || []}
                                        onChange={(features) => {
                                            setSubscriptionFeatures(prev => ({
                                                ...prev,
                                                [index]: features
                                            }));
                                        }}
                                        applicationName={watch(`applicationSubscriptions.${index}.application`) || field.application || "leadCAST"}
                                        showApplicationSelector={false}
                                    />
                                    <button type="button" onClick={() => {
                                        appRemove(index);
                                        setSubscriptionFeatures(prev => {
                                            const newState = { ...prev };
                                            delete newState[index];
                                            const reindexed: {[key: number]: Feature[]} = {};
                                            Object.keys(newState).forEach(key => {
                                                const keyNum = parseInt(key);
                                                if (keyNum > index) {
                                                    reindexed[keyNum - 1] = newState[keyNum];
                                                } else {
                                                    reindexed[keyNum] = newState[keyNum];
                                                }
                                            });
                                            return reindexed;
                                        });
                                    }} className={styles["cnclButton"]} style={{width: "100px", height: "35px", fontSize: "14px"}}>
                                        Delete
                                    </button>
                                    <hr />
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => appAppend({ application: 'leadCAST', licenseLevel: "" })} className={styles["button"]} style={{width: "200px", height: "35px", fontSize: "14px"}}>
                            Add App Subscription
                        </button>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Tags</h3>
                        {tagFields.map((field, index) => {
                            return (
                                <div key={field.id}>
                                    <label className={styles["label"]}>Name</label>
                                    <input
                                        {...register(`tags.${index}.name`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Value</label>
                                    <input
                                        {...register(`tags.${index}.value`)}
                                        className={styles["input"]}
                                    />
                                    <button type="button" onClick={() => tagRemove(index)} className={styles["cnclButton"]} style={{width: "100px", height: "35px", fontSize: "14px"}}>
                                        Delete
                                    </button>
                                    <hr />
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => tagAppend({ name: "", value: "" })} className={styles["button"]} style={{width: "150px", height: "35px", fontSize: "14px"}}>
                            Add Tags
                        </button>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Public Water Systems</h3>
                        {pwsFields.map((field, index) => {
                            return (
                                <div key={field.id}>
                                    <label className={styles["label"]}>PWS ID</label>
                                    <input
                                        {...register(`publicWaterSystems.${index}.pwsId`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Alias</label>
                                    <input
                                        {...register(`publicWaterSystems.${index}.alias`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Name</label>
                                    <input
                                        {...register(`publicWaterSystems.${index}.name`)}
                                        className={styles["input"]}
                                    />
                                    <label className={styles["label"]}>Begin Date</label>
                                    <input
                                        {...register(`publicWaterSystems.${index}.beginOn`)}
                                        className={styles["input"]}
                                        type="date"
                                    />
                                    <button type="button" onClick={() => pwsRemove(index)} className={styles["cnclButton"]} style={{width: "100px", height: "35px", fontSize: "14px"}}>
                                        Delete
                                    </button>
                                    <hr />
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => pwsAppend({ pwsId: "", alias: "", name: "", beginOn: "" })} className={styles["button"]} style={{width: "250px", height: "35px", fontSize: "14px"}}>
                            Add Public Water System
                        </button>
                        <hr />

                        <h3 style={{ display: "flex", paddingTop: "20px" }}>Location</h3>
                        <label className={styles["label"]}>Latitude</label>
                        <input
                            {...register("location.latitude")}
                            className={styles["input"]}
                            type="number"
                            step="any"
                            defaultValue={data?.getTenantById?.location?.latitude || ""}
                        />
                        <label className={styles["label"]}>Longitude</label>
                        <input
                            {...register("location.longitude")}
                            className={styles["input"]}
                            type="number"
                            step="any"
                            defaultValue={data?.getTenantById?.location?.longitude || ""}
                        />
                        <div>
                            <label className={styles["label"]}>Zoom</label>
                            <input
                                {...register("location.zoom")}
                                className={styles["input"]}
                                type="number"
                                step="any"
                                defaultValue={data?.getTenantById?.location?.zoom || ""}
                            />
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpenEditModal(false);
                                }}
                                className={styles["button"]}
                            >
                                Cancel
                            </button>
                            <input
                                type="submit"
                                className={styles["button"]}
                                value={mutationLoading ? "Updating..." : "Update"}
                                disabled={mutationLoading}
                            />
                        </div>
                    </form>
                </div>
                <ErrorDisplay error={mutationError} />
                <div className={styles["footer"]}></div>
            </div>
        </div>
    )
}

export default EditModal;
